import type { NavigateFunction } from "react-router-dom";
import type { ProfilesData } from "../../../store/useAuthStore";
import { useAuthStore } from "../../../store/useAuthStore";
import {
  hasGeniusExpectResumeOnShell,
  isGeniusDeeplinkAutoResumeEnabled,
  isGeniusShellResumePending,
  markGeniusFlowPhase,
  readPersistedGeniusDeeplinkParams,
  shouldAutoResumeGeniusDeeplink,
} from "./geniusDeeplinkSession";
import { navigateToGeniusDeepLinkTest } from "./navigateToGeniusDeepLinkTest";

let resumeInFlight = false;

export function isGeniusDeeplinkResumeInFlight(): boolean {
  return resumeInFlight;
}

/**
 * Direct-resume (skip signed-token auth) only after blank leave → shell → Genius.
 * Fresh new-tab Genius URLs always re-auth (see resetStickyGeniusResumeForFreshOpen).
 */
export function shouldResumeGeniusDeeplinkDirectly(
  profilesData?: ProfilesData | null,
): boolean {
  if (!shouldAutoResumeGeniusDeeplink(profilesData)) return false;
  return hasGeniusExpectResumeOnShell();
}

function canResumeGeniusDeeplinkDestination(
  profilesData?: ProfilesData | null,
): boolean {
  if (!isGeniusDeeplinkAutoResumeEnabled()) return false;
  if (!readPersistedGeniusDeeplinkParams()) return false;
  if (useAuthStore.getState().token == null) return false;
  return (
    shouldAutoResumeGeniusDeeplink(profilesData) ||
    isGeniusShellResumePending() ||
    hasGeniusExpectResumeOnShell()
  );
}

export async function resumeGeniusDeeplinkToDestination(
  navigate: NavigateFunction,
  profilesData?: ProfilesData | null,
): Promise<boolean> {
  const currentProfilesData =
    profilesData ?? useAuthStore.getState().profilesData;

  if (!canResumeGeniusDeeplinkDestination(currentProfilesData)) {
    return false;
  }

  const params = readPersistedGeniusDeeplinkParams();
  if (!params) {
    return false;
  }

  if (resumeInFlight) {
    return false;
  }

  resumeInFlight = true;
  try {
    await navigateToGeniusDeepLinkTest(
      params,
      navigate,
      currentProfilesData as ProfilesData | null,
    );
    markGeniusFlowPhase("completed");
    return true;
  } catch (error) {
    console.error("[GeniusDeeplink] resume to destination failed:", error);
    return false;
  } finally {
    resumeInFlight = false;
  }
}
